import { Typography } from '@components/style';
import { theme } from '@styles/theme';
import { FC, useEffect, useState } from 'react';

type MinMaxNumericInputFieldProps = {
  value: number;
  minPossibleValue?: number;
  maxPossibleValue?: number;
  handleInput: (value: number) => void;
  handleError: (value: boolean) => void;
  handleKey?: () => void;
  label: string;
  showLabel?: boolean;
  isAutoFocussed?: boolean;
  cssClass?: string;
  maxLength?: number;
  inputType?: string;
  allowEmptyValue?: boolean;
};

const NumericInputField: FC<MinMaxNumericInputFieldProps> = ({
  value,
  minPossibleValue,
  maxPossibleValue,
  label,
  showLabel = true,
  isAutoFocussed,
  cssClass,
  maxLength,
  inputType,
  handleInput,
  handleError,
  handleKey,
  allowEmptyValue = true,
}) => {
  const [currentValue, setCurrentValue] = useState<number>(null);
  const [hasError, setHasError] = useState<boolean>();
  const validate = (event): boolean => {
    const value: string = event.target.value;
    if (value === undefined || value === '') {
      setCurrentValue(null);

      if (allowEmptyValue) {
        setHasError(false);
        handleError(false);
        return true;
      }

      setHasError(true);
      handleError(true);
      handleInput(null);
      return false;
    }
    const parsedValue = (inputType !== 'float' ? parseInt(value) : parseFloat(value)) || 0;
    if (parsedValue < 0) {
      return false;
    }
    validateValue(parsedValue);
    return true;
  };

  const onKeyDown = (event): boolean => {
    if (event.key === 'Enter' && handleKey) {
      handleKey();
    }
    if (event.key === '.' && inputType !== 'float') {
      if (event.preventDefault) {
        event.preventDefault();
      } else {
        event.returnValue = false;
      }
      return false;
    }
    return true;
  };

  const validateValue = (parsedValue: number) => {
    if (
      (minPossibleValue && parsedValue < minPossibleValue) ||
      (!(maxPossibleValue === undefined || maxPossibleValue === null) &&
        parsedValue > maxPossibleValue)
    ) {
      setHasError(true);
      handleError(true);
    } else {
      setHasError(false);
      handleError(false);
    }
    setCurrentValue(parsedValue);
    handleInput(parsedValue);
  };

  const handleBlur = (event) => {
    if (!hasError && currentValue !== value) {
      handleInput(currentValue);
    }
  };

  useEffect(() => {
    if (hasError) {
      validateValue(currentValue);
      handleInput(currentValue);
    }
  }, [minPossibleValue, maxPossibleValue]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <div className={`${cssClass} unflex numeric-field`}>
      {showLabel && (
        <label className='form-label'>
          <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.semibold}>
            {label}
          </Typography>
        </label>
      )}
      <input
        data-testid='numeric-input-field'
        type={inputType !== 'float' ? 'tel' : 'number'}
        maxLength={maxLength ?? 0}
        className={`form-control ${hasError ? 'input-error' : ''}`}
        value={currentValue === null || currentValue === undefined ? '' : currentValue.toString()}
        onChange={validate}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        autoFocus={isAutoFocussed}
      />
    </div>
  );
};

export default NumericInputField;
